/**
 * Texterkennung (OCR) komplett im Browser über Tesseract.js – kein externer
 * Anbieter, kein API-Schlüssel, keine Datei verlässt das Gerät.
 *
 * Bibliothek + Sprachdaten (ein paar MB) laden erst beim allerersten Aufruf
 * nach; danach bleibt EIN Worker für den Rest der Sitzung bestehen (Start
 * dauert kurz, jede weitere Erkennung ist dann schnell). Deutsch + Englisch
 * gemeinsam geladen – Unterrichtsmaterial ist genauso oft englisch wie
 * deutsch (Fremdsprachen-Arbeitsblätter etc.).
 */

import type { Worker as TesseractWorker } from "tesseract.js";

let workerPromise: Promise<TesseractWorker> | null = null;

async function getWorker(): Promise<TesseractWorker> {
  if (!workerPromise) {
    workerPromise = import("tesseract.js").then(({ createWorker }) => createWorker(["deu", "eng"]));
  }
  return workerPromise;
}

/** Text eines einzelnen Bildes (Foto, gescannte Seite als Leinwand, …). */
export async function ocrImage(source: Blob | HTMLCanvasElement): Promise<string> {
  const worker = await getWorker();
  const { data } = await worker.recognize(source);
  return (data.text ?? "").trim();
}

/**
 * Text eines gescannten PDFs ohne Textebene: jede Seite wird per pdf.js auf
 * eine Leinwand gerastert und einzeln erkannt. `scale: 2` statt 1 – Tesseract
 * braucht für brauchbare Trefferquoten mehr als Bildschirmauflösung.
 *
 * ponytail: läuft Seite für Seite nacheinander, nicht parallel – ein
 * Tesseract-Worker verarbeitet ohnehin nur einen Auftrag gleichzeitig, und
 * mehrere Leinwände parallel im Speicher zu halten lohnt sich bei den
 * ohnehin scanner-langsamen Erkennungszeiten nicht.
 */
export async function ocrPdf(bytes: ArrayBuffer, onProgress?: (page: number, total: number) => void): Promise<string> {
  const pdfjs = await import("pdfjs-dist");
  const workerUrl = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl.default;
  const doc = await pdfjs.getDocument({ data: bytes }).promise;

  const parts: string[] = [];
  try {
    for (let p = 1; p <= doc.numPages; p++) {
      onProgress?.(p, doc.numPages);
      const page = await doc.getPage(p);
      try {
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvas, viewport }).promise;
        const text = await ocrImage(canvas);
        if (text) parts.push(`[S. ${p}] ${text}`);
      } finally {
        page.cleanup();
      }
    }
  } finally {
    await doc.cleanup();
  }
  return parts.join("\n");
}
